import React, { useRef, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import FormatAlignLeftRoundedIcon from '@mui/icons-material/FormatAlignLeftRounded';
import FormatAlignCenterRoundedIcon from '@mui/icons-material/FormatAlignCenterRounded';
import FormatAlignRightRoundedIcon from '@mui/icons-material/FormatAlignRightRounded';
import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CommonButton from './CommonButton';
import CommonInput from './CommonInput';
import type { EmailBuilderBlock, EmailBuilderBlockType } from '../api';
import { BLOCK_TYPE_LABELS, createDefaultBlock } from '../pages/emailBuilderHtml';

export interface EmailBlockBuilderProps {
  blocks: EmailBuilderBlock[];
  onChange: (blocks: EmailBuilderBlock[]) => void;
  disabled?: boolean;
}

const blockTypeOrder: EmailBuilderBlockType[] = ['header', 'text', 'image', 'button', 'table', 'divider'];

const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const EmailBlockBuilder: React.FC<EmailBlockBuilderProps> = ({ blocks, onChange, disabled = false }) => {
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const updateBlock = (index: number, patch: Partial<EmailBuilderBlock>): void => {
    const next = blocks.map((block, i) => (i === index ? { ...block, ...patch } : block));
    onChange(next);
  };

  const updateStyle = (index: number, stylePatch: Partial<EmailBuilderBlock['style']>): void => {
    const block = blocks[index];
    updateBlock(index, { style: { ...block.style, ...stylePatch } });
  };

  const removeBlock = (index: number): void => {
    onChange(blocks.filter((_, i) => i !== index));
  };

  const move = (index: number, direction: -1 | 1): void => {
    const target = index + direction;
    if (target < 0 || target >= blocks.length) return;
    onChange(moveItem(blocks, index, target));
  };

  const addBlock = (type: EmailBuilderBlockType): void => {
    onChange([...blocks, createDefaultBlock(type)]);
  };

  const handleDrop = (index: number): void => {
    const from = dragIndexRef.current;
    setDragOverIndex(null);
    dragIndexRef.current = null;
    if (from === null || from === index) return;
    onChange(moveItem(blocks, from, index));
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
        {blockTypeOrder.map((type) => (
          <CommonButton
            key={type}
            label={`Add ${BLOCK_TYPE_LABELS[type]}`}
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => addBlock(type)}
            disabled={disabled}
          />
        ))}
      </Stack>

      {blocks.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No blocks yet — add one above to start building the email.
        </Typography>
      )}

      <Stack spacing={1.25}>
        {blocks.map((block, index) => (
          <Box
            key={block.id}
            draggable={!disabled}
            onDragStart={() => {
              dragIndexRef.current = index;
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (dragOverIndex !== index) setDragOverIndex(index);
            }}
            onDragLeave={() => setDragOverIndex((prev) => (prev === index ? null : prev))}
            onDrop={() => handleDrop(index)}
            sx={{
              border: '1px solid',
              borderColor: dragOverIndex === index ? '#2F7E8E' : '#e0d7da',
              borderRadius: 2,
              p: 1.5,
              backgroundColor: dragOverIndex === index ? '#eaf4f8' : '#fafafa',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <DragIndicatorRoundedIcon fontSize="small" sx={{ color: '#8a99a3', cursor: disabled ? 'default' : 'grab' }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                {BLOCK_TYPE_LABELS[block.type]}
              </Typography>
              <CommonButton
                label=""
                size="small"
                variant="outlined"
                startIcon={<KeyboardArrowUpRoundedIcon />}
                onClick={() => move(index, -1)}
                disabled={disabled || index === 0}
              />
              <CommonButton
                label=""
                size="small"
                variant="outlined"
                startIcon={<KeyboardArrowDownRoundedIcon />}
                onClick={() => move(index, 1)}
                disabled={disabled || index === blocks.length - 1}
              />
              <CommonButton
                label=""
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={() => removeBlock(index)}
                disabled={disabled}
              />
            </Stack>

            <Stack spacing={1}>
              {(block.type === 'header' || block.type === 'text' || block.type === 'button') && (
                <CommonInput
                  label={block.type === 'button' ? 'Button Label' : 'Text'}
                  value={block.text || ''}
                  onChange={(value) => updateBlock(index, { text: value })}
                  disabled={disabled}
                />
              )}

              {block.type === 'button' && (
                <CommonInput
                  label="Link URL"
                  value={block.href || ''}
                  onChange={(value) => updateBlock(index, { href: value })}
                  placeholder="https://"
                  disabled={disabled}
                />
              )}

              {block.type === 'image' && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <CommonInput
                    label="Image URL"
                    value={block.src || ''}
                    onChange={(value) => updateBlock(index, { src: value })}
                    placeholder="https://"
                    disabled={disabled}
                  />
                  <CommonInput
                    label="Alt Text"
                    value={block.alt || ''}
                    onChange={(value) => updateBlock(index, { alt: value })}
                    disabled={disabled}
                  />
                  <CommonInput
                    label="Width %"
                    type="number"
                    value={String(block.widthPercent ?? 100)}
                    onChange={(value) => updateBlock(index, { widthPercent: Number(value) || 100 })}
                    disabled={disabled}
                  />
                </Stack>
              )}

              {block.type === 'table' && (
                <Stack spacing={0.75}>
                  {(block.tableRows || []).map((row, rowIndex) => (
                    <Stack direction="row" spacing={0.75} key={rowIndex} alignItems="center">
                      {row.map((cell, cellIndex) => (
                        <CommonInput
                          key={cellIndex}
                          label={rowIndex === 0 ? `Column ${cellIndex + 1} Header` : `Row ${rowIndex} Col ${cellIndex + 1}`}
                          value={cell}
                          onChange={(value) => {
                            const nextRows = (block.tableRows || []).map((r, ri) =>
                              ri === rowIndex ? r.map((c, ci) => (ci === cellIndex ? value : c)) : r,
                            );
                            updateBlock(index, { tableRows: nextRows });
                          }}
                          disabled={disabled}
                        />
                      ))}
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1}>
                    <CommonButton
                      label="Add Row"
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const colCount = block.tableRows?.[0]?.length || 2;
                        const nextRows = [...(block.tableRows || []), Array(colCount).fill('')];
                        updateBlock(index, { tableRows: nextRows });
                      }}
                      disabled={disabled}
                    />
                    <CommonButton
                      label="Remove Row"
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const nextRows = (block.tableRows || []).slice(0, -1);
                        updateBlock(index, { tableRows: nextRows });
                      }}
                      disabled={disabled || (block.tableRows || []).length <= 1}
                    />
                    <CommonButton
                      label="Add Column"
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const nextRows = (block.tableRows || []).map((r) => [...r, '']);
                        updateBlock(index, { tableRows: nextRows });
                      }}
                      disabled={disabled}
                    />
                    <CommonButton
                      label="Remove Column"
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const nextRows = (block.tableRows || []).map((r) => r.slice(0, -1));
                        updateBlock(index, { tableRows: nextRows });
                      }}
                      disabled={disabled || (block.tableRows?.[0]?.length || 0) <= 1}
                    />
                  </Stack>
                </Stack>
              )}

              {block.type !== 'divider' && block.type !== 'table' && (
                <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" useFlexGap sx={{ rowGap: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={block.style.align || 'left'}
                    onChange={(_, value) => value && updateStyle(index, { align: value })}
                    disabled={disabled}
                  >
                    <ToggleButton value="left"><FormatAlignLeftRoundedIcon fontSize="small" /></ToggleButton>
                    <ToggleButton value="center"><FormatAlignCenterRoundedIcon fontSize="small" /></ToggleButton>
                    <ToggleButton value="right"><FormatAlignRightRoundedIcon fontSize="small" /></ToggleButton>
                  </ToggleButtonGroup>

                  {(block.type === 'header' || block.type === 'text' || block.type === 'button') && (
                    <ToggleButtonGroup
                      size="small"
                      value={block.style.bold ? 'bold' : null}
                      onChange={(_, value) => updateStyle(index, { bold: value === 'bold' })}
                      disabled={disabled}
                    >
                      <ToggleButton value="bold"><FormatBoldRoundedIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                  )}

                  <Box sx={{ width: 90 }}>
                    <CommonInput
                      label="Font Size"
                      type="number"
                      value={String(block.style.fontSize ?? 14)}
                      onChange={(value) => updateStyle(index, { fontSize: Number(value) || 14 })}
                      disabled={disabled}
                    />
                  </Box>
                  <Box sx={{ width: 90 }}>
                    <CommonInput
                      label="Padding"
                      type="number"
                      value={String(block.style.padding ?? 12)}
                      onChange={(value) => updateStyle(index, { padding: Number(value) || 0 })}
                      disabled={disabled}
                    />
                  </Box>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Text Color</Typography>
                    <input
                      type="color"
                      value={block.style.color || '#243746'}
                      onChange={(event) => updateStyle(index, { color: event.target.value })}
                      disabled={disabled}
                      style={{ width: 32, height: 28, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer' }}
                    />
                  </Stack>

                  {block.type === 'button' && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="text.secondary">Button Color</Typography>
                      <input
                        type="color"
                        value={block.style.backgroundColor || '#2F7E8E'}
                        onChange={(event) => updateStyle(index, { backgroundColor: event.target.value })}
                        disabled={disabled}
                        style={{ width: 32, height: 28, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer' }}
                      />
                    </Stack>
                  )}
                </Stack>
              )}

              {block.type === 'divider' && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 90 }}>
                    <CommonInput
                      label="Thickness"
                      type="number"
                      value={String(block.style.padding ?? 1)}
                      onChange={(value) => updateStyle(index, { padding: Number(value) || 1 })}
                      disabled={disabled}
                    />
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">Color</Typography>
                    <input
                      type="color"
                      value={block.style.backgroundColor || '#e0d7da'}
                      onChange={(event) => updateStyle(index, { backgroundColor: event.target.value })}
                      disabled={disabled}
                      style={{ width: 32, height: 28, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer' }}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

export default EmailBlockBuilder;
